/**
 * Helper functions for file handling across the application
 */

// Base URL servera - za Android app relativni URL-ovi moraju biti apsolutni
const SERVER_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace('/api', '');

/**
 * Pretvara relativni path u apsolutni URL (potrebno za Android Capacitor app)
 */
export const toAbsoluteUrl = (url: string): string => {
  if (!url) return url;
  // Već apsolutni
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  if (SERVER_BASE) {
    return `${SERVER_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
  }
  return url;
};

/**
 * Extract the display name from a file string
 * Format: "filename|url" or just "filename"
 */
export const getFileDisplayName = (file: string): string => {
  if (file.includes('|')) {
    // Extract just the filename part (before |)
    const filename = file.split('|')[0];
    
    // New format: "originalname-timestamp-random.ext"
    // Old format: "taskId_timestamp_originalname.ext" or "fieldname-timestamp-random.ext"
    
    // Try to extract original name by removing timestamp-random suffix
    // Pattern: anything-digits-digits.ext -> return "anything.ext"
    const match = filename.match(/^(.+?)-\d+-\d+(\.\w+)$/);
    if (match) {
      return match[1] + match[2]; // originalname + extension
    }
    
    // Fallback: return whole filename
    return filename;
  }
  return file.split('/').pop() || file;
};

/**
 * Extract the URL from a file string
 * Format: "filename|url" or just "filename"
 */
export const getFileUrl = (file: string): string => {
  let url = file;
  if (file.includes('|')) {
    url = file.split('|')[1];
  }
  
  // Na web-u ostavi relativni path, na Android app-u pretvori u apsolutni
  const relative = toRelativePath(url);
  return toAbsoluteUrl(relative);
};

/**
 * Encode image URL to handle spaces and special characters
 * Only encodes the filename part after the last slash
 */
export const encodeImageUrl = (url: string): string => {
  if (!url) return url;
  
  const lastSlashIndex = url.lastIndexOf('/');
  if (lastSlashIndex !== -1) {
    const pathPart = url.substring(0, lastSlashIndex + 1);
    const filenamePart = url.substring(lastSlashIndex + 1);
    return pathPart + encodeURIComponent(filenamePart);
  }
  
  return encodeURIComponent(url);
};

/**
 * Convert full URL to relative path
 * Removes domain (evo.belkomliftovi.com, etc.) and keeps only /uploads/...
 */
export const toRelativePath = (url: string): string => {
  if (!url) return url;
  
  // If already relative, return as is
  if (url.startsWith('/')) return url;
  
  // Remove protocol and domain, keep only path starting with /uploads
  const match = url.match(/https?:\/\/[^\/]+(\/.+)/);
  if (match) {
    return match[1]; // Returns /uploads/...
  }
  
  return url;
};

/**
 * Check if a file is an image based on extension
 */
export const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'];
  const extension = fileName.toLowerCase().substr(fileName.lastIndexOf('.'));
  return imageExtensions.includes(extension);
};

/**
 * Check if a file is a PDF based on extension
 */
export const isPdfFile = (fileName: string): boolean => {
  const extension = fileName.toLowerCase().substr(fileName.lastIndexOf('.'));
  return extension === '.pdf';
};

/**
 * Format date time for display
 */
export const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return `${date.toLocaleDateString('sr-RS')} u ${date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Format relative time (Pre 2 h, Juče, etc.)
 */
export const formatRelativeTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Upravo sada';
    if (diffMins < 60) return `Pre ${diffMins} min`;
    if (diffHours < 24) return `Pre ${diffHours} h`;
    if (diffDays === 1) return 'Juče';
    if (diffDays < 7) return `Pre ${diffDays} dana`;
    
    // Za starije od nedelju dana, prikaži datum
    return date.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short' });
  } catch {
    return 'N/A';
  }
};
